import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Wrench, Users, Package, ShoppingCart,
  CreditCard, FileText, Building2, Settings, LogOut, RotateCcw,
  Menu, Bell, Smartphone, Shield, Download, Upload, BarChart2,
  ShoppingBag, TrendingDown, HardDrive, TrendingUp, Sun, Moon,
  MonitorSmartphone, Search, HelpCircle, Calendar, Trophy, Home, ClipboardCheck, BookOpen, Target
} from 'lucide-react';
import clsx from 'clsx';
import api from '../../utils/api';
import { useDarkMode } from '../../hooks/useDarkMode';
import BusquedaGlobal from '../BusquedaGlobal';
import SelectorTienda, { getTiendaNombre } from '../SelectorTienda';
import AvisosCampana from '../AvisosCampana';
import { datosPrueba, refrescarInstancia, descargarMisDatos, numeroWhatsApp } from '../../utils/instancia';
import { cargarEmpresa } from '../../utils/empresa';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard',     roles: ['admin','tecnico','recepcion'] },
  { to: '/reparaciones',  icon: Wrench,          label: 'Reparaciones',  roles: ['admin','tecnico','recepcion'] },
  { to: '/citas',         icon: Calendar,        label: 'Agenda',        roles: ['admin','tecnico','recepcion'] },
  { to: '/clientes',      icon: Users,           label: 'Clientes',      roles: ['admin','tecnico','recepcion'] },
  { to: '/imei',          icon: Smartphone,      label: 'IMEI',          roles: ['admin','tecnico'] },
  { to: '/inventario',    icon: Package,         label: 'Inventario',    roles: ['admin','tecnico'] },
  { to: '/almacen',       icon: Home,            label: 'Almacén casa',  roles: ['admin','tecnico','recepcion'] },
  { to: '/recuento',      icon: ClipboardCheck,  label: 'Recuento',      roles: ['admin','tecnico','recepcion'] },
  { to: '/ventas',        icon: ShoppingCart,    label: 'TPV / Ventas',  roles: ['admin','recepcion'] },
  { to: '/caja',          icon: CreditCard,      label: 'Caja',          roles: ['admin'] },
  { to: '/gastos',        icon: TrendingDown,    label: 'Gastos',        roles: ['admin'] },
  { to: '/garantias',     icon: Shield,          label: 'Garantías',     roles: ['admin','tecnico','recepcion'] },
  { to: '/presupuestos',  icon: FileText,        label: 'Presupuestos',  roles: ['admin','tecnico','recepcion'] },
  { to: '/facturacion',   icon: FileText,        label: 'Facturación',   roles: ['admin'] },
  { to: '/libro-facturas', icon: BookOpen,       label: 'Libro facturas', roles: ['admin'] },
  { to: '/tiendas',       icon: Building2,       label: 'Tiendas',       roles: ['admin'] },
  { to: '/metas',         icon: Target,          label: 'Metas y comisiones', roles: ['admin'] },
  { to: '/compras',       icon: ShoppingBag,     label: 'Compras',       roles: ['admin'] },
  { to: '/compra-dispositivos', icon: Smartphone, label: 'Compra móviles', roles: ['admin'] },
  { to: '/mayoristas',    icon: Building2,       label: 'Mayoristas',    roles: ['admin'] },
  { to: '/facturas-compra', icon: FileText,      label: 'Facturas gasto', roles: ['admin'] },
  { to: '/devoluciones',  icon: RotateCcw,       label: 'Devoluciones',  roles: ['admin','encargado','tecnico','recepcion'] },
  { to: '/rentabilidad',  icon: TrendingUp,      label: 'Rentabilidad',  roles: ['admin'] },
  { to: '/estadisticas',  icon: BarChart2,       label: 'Estadísticas',  roles: ['admin'] },
  { to: '/exportar',      icon: Download,        label: 'Exportar',      roles: ['admin'] },
  { to: '/importar',      icon: Upload,          label: 'Importar',      roles: ['admin'] },
  { to: '/auditoria',     icon: Shield,          label: 'Auditoría',     roles: ['admin'] },
  { to: '/tecnicos',      icon: Wrench,          label: 'Técnicos',      roles: ['admin'] },
  { to: '/objetivos',     icon: Trophy,          label: 'Objetivos',     roles: ['admin','tecnico'] },
  { to: '/usuarios',      icon: Users,           label: 'Usuarios',      roles: ['admin'] },
  { to: '/backup',        icon: HardDrive,       label: 'Backups',       roles: ['admin'] },
  { to: '/configuracion', icon: Settings,        label: 'Configuración', roles: ['admin'] },
];

export default function MainLayout() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarAbierto, setSidebarAbierto] = useState(false);
  const [darkMode, setDarkMode] = useDarkMode();
  const [busquedaAbierta, setBusquedaAbierta] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const notifRef = useRef(null);

  const handleLogout = () => { logout(); navigate('/login'); };
  // Copia de prueba para un cliente: aviso arriba con los días que quedan (se mira cada 10 min).
  // Y el nombre de la empresa para los mensajes.
  const [prueba, setPrueba] = useState(datosPrueba());
  const [bajando, setBajando] = useState(false);
  useEffect(() => {
    cargarEmpresa();
    if (!datosPrueba()) return undefined;
    const mirar = () => refrescarInstancia().then(p => setPrueba(p));
    mirar();
    const id = setInterval(mirar, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const bajarMisDatos = async () => {
    setBajando(true);
    try { await descargarMisDatos(); toast.success('Datos descargados'); }
    catch (e) { toast.error(e.message); }
    finally { setBajando(false); }
  };
  // Si el usuario tiene permisos personalizados, usarlos; si no, usar el rol
  const itemsVisibles = NAV_ITEMS.filter(item => {
    if (usuario?.rol === 'admin') return true; // admin ve todo siempre
    if (usuario?.permisos && Array.isArray(usuario.permisos)) {
      return usuario.permisos.includes(item.to.replace('/', ''));
    }
    return item.roles.includes(usuario?.rol);
  });

  // Notificaciones
  const cargarNotificaciones = async () => {
    try {
      const { data } = await api.get('/notificaciones');
      setNotificaciones(data.data || []);
      setNotifCount(data.total || 0);
    } catch {}
  };

  useEffect(() => {
    cargarNotificaciones();
    const interval = setInterval(cargarNotificaciones, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Cerrar notif al click fuera
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Atajo Ctrl+K búsqueda
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setBusquedaAbierta(v => !v); }
      if (e.key === 'Escape') { setBusquedaAbierta(false); setShowNotif(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); setShowInstall(true); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const instalarApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') { setShowInstall(false); toast.success('¡App instalada!'); }
    setInstallPrompt(null);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full sidebar-bg">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5" style={{borderBottom:'1px solid rgba(168,85,247,0.2)'}}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)',boxShadow:'0 0 16px rgba(168,85,247,0.5)'}}>
          <Smartphone size={17} className="text-white"/>
        </div>
        <div>
          <p className="font-bold text-sm leading-tight" style={{color: darkMode ? '#e2e8f0' : '#111827'}}>OAMATECH</p>
          <p className="text-xs font-semibold" style={{color:'#a855f7',letterSpacing:'0.08em'}}>MANAGER</p>
        </div>
      </div>

      {/* Tienda activa */}
      <div className="px-4 py-2.5 mx-3 mt-3 rounded-xl text-xs" style={{background:'rgba(168,85,247,0.12)',border:'1px solid rgba(168,85,247,0.2)',color:'#c084fc'}}>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400" style={{boxShadow:'0 0 6px #4ade80'}}/>
          {getTiendaNombre() || usuario?.tienda_nombre || 'Tienda'}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto sidebar-safe">
        {itemsVisibles.map(({ to, icon: Icon, label }) => (
          <NavLink key={`${to}-${label}`} to={to} onClick={() => setSidebarAbierto(false)}
            className={({ isActive }) => clsx(
              'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
              isActive
                ? 'text-white'
                : darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            )}
            style={({ isActive }) => isActive ? {
              background: 'linear-gradient(135deg, rgba(168,85,247,0.25), rgba(124,58,237,0.15))',
              border: '1px solid rgba(168,85,247,0.35)',
              color: '#c084fc',
              boxShadow: '0 0 12px rgba(168,85,247,0.15)'
            } : {}}>
            {({ isActive }) => (
              <>
                <Icon size={17} style={isActive ? {color:'#a855f7'} : {}}/>
                <span className="flex-1">{label}</span>
                {isActive && <div className="w-1.5 h-1.5 rounded-full" style={{background:'#a855f7',boxShadow:'0 0 6px #a855f7'}}/>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer usuario */}
      <div className="px-3 py-3" style={{borderTop:'1px solid rgba(168,85,247,0.15)'}}>
        <div className="flex items-center gap-3 px-3 py-2 mb-1">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
            style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)'}}>
            {usuario?.nombre?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{color: darkMode ? '#e2e8f0' : '#111827'}}>{usuario?.nombre}</p>
            <p className="text-xs capitalize" style={{color:'#a855f7'}}>{usuario?.rol}</p>
          </div>
        </div>
        <button onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 w-full text-sm rounded-xl transition-all"
          style={{color:'#94a3b8'}}
          onMouseEnter={e=>{e.currentTarget.style.color='#f87171';e.currentTarget.style.background='rgba(239,68,68,0.1)'}}
          onMouseLeave={e=>{e.currentTarget.style.color='#94a3b8';e.currentTarget.style.background=''}}>
          <LogOut size={16}/> Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex h-screen overflow-hidden" style={{background: darkMode ? '#0d0d1a' : '#f0f2f5'}}>
        {/* Sidebar escritorio */}
        <aside className="hidden lg:flex flex-col w-60 flex-shrink-0 sidebar-bg" style={{borderRight:'1px solid rgba(168,85,247,0.15)'}}>
          <SidebarContent/>
        </aside>

        {/* Sidebar móvil */}
        {sidebarAbierto && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarAbierto(false)}/>
            <aside className="absolute left-0 top-0 bottom-0 w-60 z-50 sidebar-bg"><SidebarContent/></aside>
          </div>
        )}

        {/* Contenido principal */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Topbar */}
          <header className="header-bg px-4 lg:px-6 py-3 flex items-center gap-3 flex-shrink-0" style={{borderBottom: darkMode ? '1px solid rgba(168,85,247,0.15)' : '1px solid rgba(0,0,0,0.06)', background: darkMode ? 'rgba(13,13,26,0.95)' : 'white', backdropFilter:'blur(20px)'}}>
            <button onClick={() => setSidebarAbierto(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-500">
              <Menu size={20}/>
            </button>

            {/* Búsqueda global */}
            <button onClick={() => setBusquedaAbierta(true)}
              className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-xl transition-all"
              style={{background: darkMode ? 'rgba(255,255,255,0.06)' : '#f3f4f6', color: darkMode ? '#64748b' : '#9ca3af', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid transparent'}}>
              <Search size={15}/>
              <span className="hidden md:inline text-xs">Buscar</span>
              <kbd className="hidden md:inline text-xs rounded px-1" style={{background: darkMode ? 'rgba(255,255,255,0.08)' : 'white', border:'1px solid rgba(0,0,0,0.1)'}}>Ctrl K</kbd>
            </button>

            <div className="flex-1"/>

            {/* Selector de tienda: cambiar desde cualquier pantalla */}
            <SelectorTienda darkMode={darkMode} />

            {/* Instalar app */}
            {showInstall && (
              <button onClick={instalarApp} className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-xl transition-all">
                <MonitorSmartphone size={14}/> Instalar app
              </button>
            )}

            {/* Modo oscuro */}
            <button onClick={() => setDarkMode(d => !d)} className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all">
              {darkMode ? <Sun size={20} className="text-yellow-400"/> : <Moon size={20}/>}
            </button>

            {/* Mis avisos personales */}
            <AvisosCampana darkMode={darkMode} />

            {/* Alertas del sistema */}
            <div className="relative" ref={notifRef}>
              <button onClick={() => setShowNotif(v => !v)} className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <Bell size={20}/>
                {notifCount > 0 && <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">{notifCount > 9 ? '9+' : notifCount}</span>}
              </button>
              {showNotif && (
                <div className="absolute right-0 top-10 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <p className="font-semibold text-gray-800 text-sm">Alertas</p>
                    <span className="text-xs text-gray-400">{notificaciones.length}</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notificaciones.length === 0 ? (
                      <div className="text-center py-8"><p className="text-2xl mb-2">✅</p><p className="text-sm text-gray-400">Todo en orden</p></div>
                    ) : notificaciones.map((n, i) => (
                      <div key={i} className={clsx('px-4 py-3 border-b border-gray-50 hover:bg-gray-50', n.nivel==='alta'?'bg-red-50':n.nivel==='media'?'bg-yellow-50':'')}>
                        <div className="flex items-start gap-2">
                          <span className="text-base flex-shrink-0">{n.tipo==='reparacion_lista'?'🔧':n.tipo==='stock_bajo'?'📦':n.tipo==='garantia_vence'?'🛡️':n.tipo==='presupuesto_pendiente'?'📋':'🚚'}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-800">{n.titulo}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{n.mensaje}</p>
                            {n.telefono && (
                              <button onClick={() => { window.open(`https://wa.me/${numeroWhatsApp(n.telefono)}`, '_blank'); setShowNotif(false); }}
                                className="text-xs text-green-600 mt-1 flex items-center gap-1">📱 WhatsApp</button>
                            )}
                          </div>
                          <span className={clsx('text-xs px-1.5 py-0.5 rounded-full flex-shrink-0', n.nivel==='alta'?'bg-red-100 text-red-700':n.nivel==='media'?'bg-yellow-100 text-yellow-700':'bg-gray-100 text-gray-500')}>{n.nivel}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                    <button onClick={cargarNotificaciones} className="text-xs text-blue-600">Actualizar</button>
                  </div>
                </div>
              )}
            </div>
          </header>

          {/* Páginas */}
          {prueba && prueba.activa && (
            <div className="bg-blue-600 text-white text-xs font-semibold text-center py-1 px-3 flex-shrink-0 flex flex-wrap items-center justify-center gap-x-3 gap-y-1" data-prueba-banner>
              <span>
                Versión de prueba de OAMATECH ERP ·{' '}
                {prueba.diasRestantes <= 1 ? 'hoy es el último día' : `te quedan ${prueba.diasRestantes} días (hasta el ${prueba.ultimoDia})`}
              </span>
              {usuario?.rol === 'admin' && !prueba.exportAutorizada && (
                <NavLink to="/configuracion" className="underline font-normal">Pon los datos de tu empresa en Configuración</NavLink>
              )}
              {usuario?.rol === 'admin' && prueba.exportAutorizada && (
                <button onClick={bajarMisDatos} disabled={bajando} className="inline-flex items-center gap-1 bg-white text-blue-700 rounded px-2 py-0.5">
                  <Download size={12}/> {bajando ? 'Preparando…' : 'Descargar mis datos'}
                </button>
              )}
            </div>
          )}
          <main className="flex-1 overflow-y-auto p-4 lg:p-6">
            <Outlet/>
          </main>
        </div>
      </div>

      {/* Búsqueda global overlay */}
      {busquedaAbierta && <BusquedaGlobal onClose={() => setBusquedaAbierta(false)}/>}
    </>
  );
}
